import { message as toast } from "antd";
import { Employee } from "@/types/chat";
import { convertEmployeeToString, employeeToUid } from "@/utils/chat/message";
import { FocusSDK } from "@/utils";
import { getJRTCToken } from "@/api/meeting";
import { openUserSelectionModal } from "@/utils/modals";
import { mapGroupRosters } from "../../GroupUserSelector/common";
import { UserSelectionResult } from "@jd/focus-desktop-comps/lib/es/UserSelectionModal";
import { AVSDK } from "@/baseComponents/Meeting/common";
import { NetType } from "@/components/Modals/ChangeNetModal";
import i18n from "i18next";
import config, { ConfigEnum } from "@/config/config";
import {
  handleCreateSingleMeeting,
  getApplicationId,
  avsdkNameMap
} from "@/baseComponents/Meeting/utils";
import { syncConfirmNew } from "@/utils/syncConfirm";
import { handleCreateSingleMeeting as handleCreateSingleZoomMeeting } from "@/components/Meeting/utils";
import { getAppApplyStatus, AppTrialStatus } from "@/components/ThirdApp/utils";

const handleCreateSingleJDMeeting = async (
  selectedSession: any,
  currentEmployee: Employee,
  type?: string
) => {
  if (type !== "voice") {
    const appApplyStatus = await getAppApplyStatus(getApplicationId(AVSDK.JBRTC), true);
    if (appApplyStatus !== AppTrialStatus.NORMAL) {
      toast.warn("请您从工作台进入智能会议应用申请试用权限");
      return;
    }
  }
  const typeDesc = type === "voice" ? "语音通话" : "视频通话";
  const res = await syncConfirmNew(`是否发起${typeDesc}？`, typeDesc);
  if (!res) {
    return;
  }
  const { userId, teamId, app, name, avatar } = selectedSession.info as Employee;
  handleCreateSingleMeeting({
    from: {
      app: currentEmployee.app,
      teamId: currentEmployee.teamId,
      pin: currentEmployee.userId,
      avatar: currentEmployee.avatar,
      name: currentEmployee.name
    },
    to: { pin: userId, teamId, app, avatar, name },
    avsdk: AVSDK.JBRTC,
    type
  }).catch((e) => {
    console.error(e);
    toast.error(e?.message || e || "创建会议失败");
  });
};

async function handleCreateGroupMeetingClick(
  groupRosters: any,
  userData: any,
  currentEmployee: any
) {
  const avsdk = config[ConfigEnum.CHAT_SESSION_CREATE_JD_VIDEO_ENABLE] ? AVSDK.JBRTC : AVSDK.ZOOM;
  const appApplyStatus = await getAppApplyStatus(getApplicationId(avsdk), true);
  if (appApplyStatus !== AppTrialStatus.NORMAL) {
    toast.warn(`请您从工作台进入${avsdkNameMap[avsdk]}应用申请试用权限`);
    return;
  }
  const groupMembers = mapGroupRosters(groupRosters, currentEmployee, { visible: true });
  openUserSelectionModal(
    {
      title: i18n.t("chat:add_member"),
      tabs: ["custom"],
      // tabs: ['business'],
      // businessTab: {
      //     tabName: i18n.t('chat:add_member'),
      //     tabParam: {
      //         businessType: 'group',
      //         businessId: selectedSession.sessionId,
      //     },
      // },
      maxSelectedNum: 100,
      canSelectAll: true,
      resultUsers: [
        {
          id: convertEmployeeToString(currentEmployee),
          avatar: currentEmployee.avatar,
          checked: true,
          name: currentEmployee.name,
          teamId: currentEmployee.teamId,
          userId: currentEmployee.userId,
          visible: true
        }
      ],
      frozenIds: [convertEmployeeToString(currentEmployee)],
      // customTab: {
      //     tabName: i18n.t('chat:group_members'),
      //     dataList: groupMembers,
      // },
      customTabs: [
        {
          canSelectAll: true,
          tabName: i18n.t("chat:current_group_member"),
          dataList: groupMembers
        }
      ],
      currentUser: employeeToUid(currentEmployee)
    },
    (data: UserSelectionResult, close: () => void) => {
      const { result } = data.data;
      const users = result.map((r) => {
        const tem = r.id.split(":");
        return {
          realName: r.name,
          userId: tem[0],
          teamId: tem[2],
          appId: tem[1],
          headImg: r.avatar as string
        };
      });
      if (config[ConfigEnum.CHAT_SESSION_CREATE_JD_VIDEO_ENABLE]) {
        handleCreateGroupJDMeeting(currentEmployee, users);
      } else {
        handleCreateGroupMeeting(users);
      }
      close();
    },
    userData
  );
}

export const handleCreateGroupJDMeeting = (currentEmployee: any, participants: any[]) => {
  getJRTCToken({
    currentEmployee
  }).then(([jrcToken, err]) => {
    if (err) {
      toast.error(err);
      return;
    }

    const { app, userId, teamId, name, avatar, deptName } = currentEmployee;

    const userInfo = {
      token: jrcToken.token,
      deviceType: jrcToken.deviceType,
      jrtcUserId: jrcToken.jrtcUserId,
      appId: jrcToken.appId,
      app,
      pin: userId,
      teamId,
      name,
      avatar,
      deptName
    };
    FocusSDK.sendIpcMessage("jdmeet.showCreate", {
      userInfo,
      participants,
      env: {
        domain: jrcToken.domain,
        rtcDomain: jrcToken.rtcDomain
      }
    });
  });
};
const handleCreateZoomMeeting = async (
  newPublicResult: boolean,
  selectedSession: any,
  currentEmployee: Employee
) => {
  const appApplyStatus = await getAppApplyStatus(getApplicationId(AVSDK.ZOOM), true);
  if (appApplyStatus !== AppTrialStatus.NORMAL) {
    toast.warn("请您从工作台进入视频会议应用申请试用权限");
    return;
  }
  if (FocusSDK.getCurrentNetType() === NetType.PRIVATE_NET) {
    if (!newPublicResult) {
      toast.warn(i18n.t("common:joinMeeting.private net"));
      return;
    }
  }
  const res = await syncConfirmNew("是否发起视频通话？", "视频通话");
  if (!res) {
    return;
  }
  const { userId, teamId, app, name, avatar } = selectedSession.info as Employee;

  handleCreateSingleZoomMeeting({
    from: {
      pin: currentEmployee.userId,
      name: currentEmployee.name,
      teamId: currentEmployee.teamId,
      app: currentEmployee.app,
      avatar: currentEmployee.avatar
    },
    to: {
      name,
      teamId,
      pin: userId,
      app,
      avatar
    }
  }).catch((e) => {
    toast.error(e?.message || e || "创建会议失败");
  });
};

export function handleCreateGroupMeeting(participants: any[]) {
  // 这里用focussdk，是因为群聊有可能从新窗口打开，所以消息需要从桌面端中转一下，在主窗口打开modal
  FocusSDK.sendToMainWin({
    type: "meeting:create-show",
    data: {
      list: participants
    }
  });
  // 主窗口 聚焦
  FocusSDK.sendMainMessage("focusMainWin", {});
}

export function handleMeetingClickOperation({
  selectedSession,
  currentEmployee,
  newPublicResult,
  globalContext,
  type
}: {
  selectedSession: any;
  currentEmployee: any;
  newPublicResult: boolean;
  globalContext: {
    groupRosters: any;
    userData: any;
  };
  type?: string;
}) {
  const { userData, groupRosters } = globalContext;
  if (selectedSession.isSingle) {
    // 单聊
    if (config[ConfigEnum.CHAT_SESSION_CREATE_JD_VIDEO_ENABLE] || type === "voice") {
      handleCreateSingleJDMeeting(selectedSession, currentEmployee, type);
    } else {
      handleCreateZoomMeeting(newPublicResult, selectedSession, currentEmployee);
    }
  } else {
    // 群聊
    handleCreateGroupMeetingClick(groupRosters, userData, currentEmployee);
  }
}
